import React, { useState, useEffect } from 'react';
import { OptionalState } from '../../utils/ReactHelper';
import Linker from '../../library/Linker';
import { Button, ButtonGroup, Col, Container, Form, InputGroup, Row, Table } from 'react-bootstrap';
import Chart, { ChartConfiguration } from 'chart.js/auto';
import Logger from '../../../common/utils/Logger';
import { ChartData } from '../../../common/Chart';
import { GameID, GameIDs, GameIDStrings } from '../../../common/Game';
import { Entry, EntryFilter, EntryGetRequest, EntryScoreGroup, EntrySort, EntryTopRecords } from '../../../common/Entry';
import Report, { ReportStrings, ReportValues } from '../../../common/Report';
import { BGVariant, ButtonStyle, ButtonVariant } from '../../utils/BootstrapHelper';
import { FaArrowUp, FaArrowDown, FaPage4, FaBook } from 'react-icons/fa';
import CommonHelper from '../../../common/utils/CommonHelper';
import { InputTags } from './InputTags';
import { Multiselect } from './Multiselect';
import moment from "moment";
import SortType from '../../../common/Sort';
import { CompID, CompIDs, CompIDStrings } from '../../../common/Competency';
//import { SiteID, SiteIDStrings, SiteIDs } from '../../../common/Site';

type Props = {
    linker: Linker;
};

type State = {
    isInitialized: boolean;
    chartAverageScores: number;
    chartGroupedScores: number;
    chartMonthly: number;
    chartSiteAttempts: number;
    totalMonthlyAttempts: number;
    totalMonthlyUsers: number;
    entries: Entry[];
    isBusy: boolean;
    sortType: SortType;
    sort: EntrySort;
    page: number;
    pageJump: number;
    pages: number;
    limit: number;
    filter: EntryFilter;
    scoreGroup: EntryScoreGroup[],
    summary: EntryTopRecords,
    resetFilterGameIDs: boolean;
    resetFilterSiteIDs: boolean;
    resetFilterCompIDs: boolean;
};

const StateDefault: State = {
    isInitialized: false,
    isBusy: false,
    chartAverageScores: -1,
    chartGroupedScores: -1,
    chartMonthly: -1,
    chartSiteAttempts: -1,
    entries: [],
    page: 1,
    pageJump: 0,
    limit: 10,
    pages: 1,
    sort: EntrySort.Timestamp,
    sortType: SortType.Descending,
    totalMonthlyAttempts: 0,
    totalMonthlyUsers: 0,
    filter: {},
    scoreGroup: [
        { min: 0, max: 60, },
        { min: 61, max: 85, },
        { min: 86, max: 100, },
    ],
    summary: {},
    resetFilterGameIDs: false,
    resetFilterSiteIDs: false,
    resetFilterCompIDs: false,
};

const chartIDs: string[] = [
    'pieChart',
    'barChart1',
    'lineChart',
    'barChart2'
];

const Charts = (props: Props) => {

    const [state, setState] = useState(StateDefault);

    const updateState = (updates: Partial<State>) => {
        setState(OptionalState(state, updates));
    };

    useEffect(() => {
        // doEntriesGet({
        //     filter: state.filter,
        //     limit: state.limit,
        //     page: state.page,
        //     sorts: [[state.sort, state.sortType]],
        //     scoreGroup: state.scoreGroup,
        // });
    }, []);

    const showChartGroupedScores = async (data: ChartData[]): Promise<number> => {

        if (state.chartGroupedScores != -1) {
            Chart.instances[state.chartGroupedScores].destroy();
        }

        let title: string = 'Grouped Score';

        let chartElm: HTMLCanvasElement = document.getElementById(chartIDs[0]) as HTMLCanvasElement;
        let chartConfig: ChartConfiguration = {
            type: 'pie',
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: title,
                        position: 'top',
                        color: 'black'
                    }
                }
            },
            data: {
                labels: data.map(element => element.key),
                datasets: [
                    {
                        label: 'Number of Users',
                        data: data.map(element => element.value)
                    }
                ]
            }
        };
        let chart = new Chart(chartElm, chartConfig);

        return Promise.resolve(chart.id as unknown as number);
    };

    const showChartAverageScores = async (data: ChartData[]): Promise<number> => {

        if (state.chartAverageScores != -1) {
            Chart.instances[state.chartAverageScores].destroy();
        }

        let title: string = 'Average Score';

        let chartElm: HTMLCanvasElement = document.getElementById(chartIDs[1]) as HTMLCanvasElement;
        let chartConfig: ChartConfiguration = {
            type: 'bar',
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: title,
                        position: 'top',
                        color: 'black'
                    }
                }
            },
            data: {
                labels: data.map(element => element.key),
                datasets: [
                    {
                        label: 'Average Score',
                        data: data.map(element => element.value)
                    }
                ]
            }
        };
        let chart = new Chart(chartElm, chartConfig);

        return Promise.resolve(chart.id as unknown as number);
    };

    const showChartMonthly = async (attempts: ChartData[], users: ChartData[]): Promise<number> => {

        if (state.chartMonthly != -1) {
            Chart.instances[state.chartMonthly].destroy();
        }

        let title: string = 'Monthly Attempts and Users';

        let chartElm: HTMLCanvasElement = document.getElementById(chartIDs[2]) as HTMLCanvasElement;
        let chartConfig: ChartConfiguration = {
            type: 'line',
            options: {
                elements: {
                    point: {
                        radius: 10,
                        hitRadius: 20,
                        hoverRadius: 20
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: title,
                        position: 'top',
                        color: 'black'
                    }
                }
            },
            data: {
                labels: attempts.map(element => element.key),
                datasets: [
                    {
                        label: 'Attempts',
                        data: attempts.map(element => element.value)
                    },
                    {
                        label: 'Users',
                        data: users.map(element => element.value)
                    }
                ]
            }
        };
        let chart = new Chart(chartElm, chartConfig);

        return Promise.resolve(chart.id as unknown as number);
    };

    const showChartSiteAttempts = async (data: ChartData[]): Promise<number> => {

        if (state.chartSiteAttempts != -1) {
            Chart.instances[state.chartSiteAttempts].destroy();
        }

        let title: string = 'Site Attempts';

        let chartElm: HTMLCanvasElement = document.getElementById(chartIDs[3]) as HTMLCanvasElement;
        let chartConfig: ChartConfiguration = {
            type: 'bar',
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: title,
                        position: 'top',
                        color: 'black'
                    }
                }
            },
            data: {
                labels: data.map(element => element.key),
                datasets: [
                    {
                        label: 'Number of Attempts',
                        data: data.map(element => element.value)
                    }
                ]
            }
        };
        let chart = new Chart(chartElm, chartConfig);

        return Promise.resolve(chart.id as unknown as number);
    };

    const doEntriesGet = async (values?: Partial<EntryGetRequest>) => {

        updateState({
            isBusy: true
        });

        if (values == undefined) {
            values = {};
        }
        if (values.filter == undefined) {
            values.filter = state.filter;
        }
        if (values.limit == undefined) {
            values.limit = state.limit;
        }
        if (values.page == undefined) {
            values.page = state.page;
        }
        if (values.scoreGroup == undefined) {
            values.scoreGroup = state.scoreGroup;
        }
        if (values.sorts == undefined) {
            values.sorts = [[state.sort, state.sortType]];
        }
        if (values.limitTopRecords == undefined) {
            values.limitTopRecords = 5;
        }

        let response = await props.linker.EntriesGet(values as unknown as EntryGetRequest);

        let averageScores: ChartData[] = response.value!.chart.averageScores.map<ChartData>(e => {
            let gameID: GameID = e[0];
            let averageScore: number = e[1];
            return {
                key: GameIDStrings[gameID],
                value: averageScore
            };
        });

        let groupedScores: ChartData[] = response.value!.chart.groupedScores.map<ChartData>(e => {
            let scoreGroup: EntryScoreGroup = e[0];
            let numberOfUsers: number = e[1];
            return {
                key: `Score: ${scoreGroup.min} - ${scoreGroup.max}`,
                value: numberOfUsers
            };
        });

        let monthlyAttempts: ChartData[] = response.value!.chart.monthlyAttempts.map<ChartData>(e => {
            let dateString: string = moment(new Date(e[0])).format("MMM YYYY");
            let numberOfAttempts: number = e[1];
            return {
                key: dateString,
                value: numberOfAttempts
            };
        });

        let monthlyUsers: ChartData[] = response.value!.chart.monthlyUsers.map<ChartData>(e => {
            let dateString: string = moment(new Date(e[0])).format("MMM YYYY");
            let numberOfUsers: number = e[1];
            return {
                key: dateString,
                value: numberOfUsers
            };
        });

        let siteAttempts: ChartData[] = response.value!.chart.siteAttempts.map<ChartData>(e => {
            let siteId: string = e[0];
            let numberOfAttempts: number = e[1];
            return {
                key: siteId,
                value: numberOfAttempts
            };
        });

        let chartAverageScores: number = await showChartAverageScores(averageScores);
        let chartGroupedScores: number = await showChartGroupedScores(groupedScores);
        let chartSiteAttempts: number = await showChartSiteAttempts(siteAttempts);
        let chartMonthly: number = await showChartMonthly(monthlyAttempts, monthlyUsers);

        let totalMonthlyUsers: number = 0;
        let totalMonthlyAttempts: number = 0;
        response.value!.chart.monthlyAttempts.forEach(e => {
            totalMonthlyAttempts += e[1];
        });
        response.value!.chart.monthlyUsers.forEach(e => {
            totalMonthlyUsers += e[1];
        });

        updateState({
            isBusy: false,
            entries: response.value!.entries,
            sort: values.sorts[0][0],
            sortType: values.sorts[0][1],
            page: response.value!.page,
            pages: response.value!.pages,
            chartAverageScores: chartAverageScores,
            chartGroupedScores: chartGroupedScores,
            chartMonthly: chartMonthly,
            chartSiteAttempts: chartSiteAttempts,
            totalMonthlyAttempts: totalMonthlyAttempts,
            totalMonthlyUsers: totalMonthlyUsers,
            pageJump: 0,
            summary: response.value!.topRecords,
        });

        return Promise.resolve();

    };

    const getReportValueString = (report: Report): string[] => {
        let output: string[] = Array
            .from(ReportValues, (_, index: number) => ((report & ReportValues[index]) == ReportValues[index]) ? ReportStrings[index] : undefined)
            .filter((e) => e != undefined) as string[];
        return output;
    };

    const doSort = (sort: EntrySort) => {
        let sortType: SortType = state.sortType == SortType.Ascending ? SortType.Descending : SortType.Ascending;
        if (state.sort != sort) {
            sortType = SortType.Descending;
        }
        doEntriesGet({
            sorts: [[sort, sortType]],
        });
    };

    const doPage = (page: number) => {
        if (page < 1) page = 1;
        doEntriesGet({
            page: page,
        });
    };

    const doFormatDate = (input?: Date | number): string => {
        if (input == undefined) {
            return '';
        }
        if (typeof input == 'number') {
            input = new Date(input);
        }
        return `${input.getFullYear()}-${(input.getMonth() + 1).toString().padStart(2, '0')}-${input.getDate().toString().padStart(2, '0')}`;
    };

    const currentPaging = CommonHelper.Paging(state.page, state.pages);

    const doExport = async () => {

        updateState({
            isBusy: true
        });

        await props.linker.Export({
            filter: state.filter,
            sorts: [[state.sort, state.sortType]]
        });
    };

    return (
        <>
            <Container>
                <Row xs={1} sm={2}>
                    <Col>
                        <Form noValidate onSubmit={() => doEntriesGet}>
                            <Form.Group className="mb-3" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>From Date</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <Form.Control className="rounded-start-5 rounded-end-5" type="date" value={doFormatDate(state.filter.fromDate)}
                                        onChange={(e) => {
                                            updateState({
                                                filter: { ...state.filter, fromDate: new Date(e.target.value).getTime() }
                                            });
                                        }} />
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, fromDate: undefined }
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>To Date</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <Form.Control className="rounded-start-5 rounded-end-5" type="date" value={doFormatDate(state.filter.toDate)}
                                        onChange={(e) => {
                                            updateState({
                                                filter: { ...state.filter, toDate: new Date(e.target.value).getTime() }
                                            });
                                        }} />
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, toDate: undefined }
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formChart.gameID" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>Game IDs</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <Multiselect
                                        options={GameIDs.map((gameID: GameID) => {
                                            return {
                                                gameID: gameID,
                                                gameIDString: GameIDStrings[gameID]
                                            };
                                        })}
                                        onSelect={(list: { gameID: GameID, gameIDString: string }[], e) => {
                                            updateState({
                                                filter: { ...state.filter, gameIDs: list.map(element => element.gameID) },
                                                resetFilterGameIDs: false,
                                            });
                                        }}
                                        onRemove={(list: { gameID: GameID, gameIDString: string }[], e) => {
                                            updateState({
                                                filter: { ...state.filter, gameIDs: list.map(element => element.gameID) },
                                                resetFilterGameIDs: false,
                                            });
                                        }}
                                        displayValue="gameIDString" // Property name to display in the dropdown options, if isObject is true
                                        isObject={true}
                                        placeholder=''
                                        containerClassName={`${ButtonStyle.RoundedLeft} ${ButtonStyle.RoundedRight}`}
                                        elementClassName={`${ButtonStyle.RoundedLeft} ${ButtonStyle.RoundedRight}`}
                                        selectedValues={state.filter.gameIDs && Array.from(state.filter.gameIDs, (gameID: GameID) => ({
                                            gameID: gameID,
                                            gameIDString: GameIDStrings[gameID]
                                        })) || undefined}
                                        resetSelectedValues={state.resetFilterGameIDs}
                                    />
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, gameIDs: undefined },
                                            resetFilterGameIDs: true,
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formChart.compID" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>Competency IDs</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <Multiselect
                                        options={CompIDs.map((compID: CompID) => {
                                            return {
                                                compID: compID,
                                                compIDString: CompIDStrings[compID]
                                            };
                                        })}
                                        onSelect={(list: { compID: CompID, compIDString: string }[], e) => {
                                            updateState({
                                                filter: { ...state.filter, compIDs: list.map(element => element.compID) },
                                                resetFilterCompIDs: false,
                                            });
                                        }}
                                        onRemove={(list: { compID: CompID, compIDString: string }[], e) => {
                                            updateState({
                                                filter: { ...state.filter, compIDs: list.map(element => element.compID) },
                                                resetFilterCompIDs: false,
                                            });
                                        }}
                                        displayValue="compIDString" // Property name to display in the dropdown options, if isObject is true
                                        isObject={true}
                                        placeholder=''
                                        containerClassName={`${ButtonStyle.RoundedLeft} ${ButtonStyle.RoundedRight}`}
                                        elementClassName={`${ButtonStyle.RoundedLeft} ${ButtonStyle.RoundedRight}`}
                                        selectedValues={state.filter.compIDs && Array.from(state.filter.compIDs, (compID: CompID) => ({
                                            compID: compID,
                                            compIDString: CompIDStrings[compID]
                                        })) || undefined}
                                        resetSelectedValues={state.resetFilterCompIDs}
                                    />
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, compIDs: undefined },
                                            resetFilterCompIDs: true,
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formChart.siteID" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>Site ID Contains</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <InputTags className="rounded-start-5 rounded-end-5" elementClassName={`rounded-start-5 rounded-end-5 ${BGVariant.Primary}`}
                                        values={state.filter.siteIDs && state.filter.siteIDs || []}
                                        onTags={(value) => {
                                            updateState({
                                                filter: { ...state.filter, siteIDs: value.values }
                                            });
                                        }}></InputTags>
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, siteIDs: undefined },
                                            resetFilterSiteIDs: true,
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formChart.divID" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>Division ID Contains</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <InputTags className="rounded-start-5 rounded-end-5" elementClassName={`rounded-start-5 rounded-end-5 ${BGVariant.Primary}`}
                                        values={state.filter.divIDs && state.filter.divIDs || []}
                                        onTags={(value) => {
                                            updateState({
                                                filter: { ...state.filter, divIDs: value.values }
                                            });
                                        }}></InputTags>
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, divIDs: undefined }
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formChart.userIDs" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>User ID Contains</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <InputTags className="rounded-start-5 rounded-end-5" elementClassName={`rounded-start-5 rounded-end-5 ${BGVariant.Primary}`}
                                        values={state.filter.userIDContains && state.filter.userIDContains || []}
                                        onTags={(value) => {
                                            updateState({
                                                filter: { ...state.filter, userIDContains: value.values }
                                            });
                                        }}></InputTags>
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, userIDContains: undefined }
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formChart.userNames" as={Row}>
                                <Col sm='5' md='4' lg='3'>
                                    <Form.Label column>Name Contains</Form.Label>
                                </Col>
                                <Col sm='auto'>
                                    <InputTags className="rounded-start-5 rounded-end-5" elementClassName={`rounded-start-5 rounded-end-5 ${BGVariant.Primary}`}
                                        values={state.filter.userNameContains && state.filter.userNameContains || []}
                                        onTags={(value) => {
                                            updateState({
                                                filter: { ...state.filter, userNameContains: value.values }
                                            });
                                        }}></InputTags>
                                </Col>
                                <Col sm='5' md='4' lg='3'>
                                    <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                        updateState({
                                            filter: { ...state.filter, userNameContains: undefined }
                                        });
                                    }}>Clear</Button>
                                </Col>
                            </Form.Group>
                            <Button className="rounded-start-5 rounded-end-5" onClick={() => {
                                doEntriesGet();
                            }}>Filter</Button>
                        </Form>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {
                            state.summary.userBestScores &&
                            <Table responsive striped borderless size='sm' className='text-center'>
                                <thead>
                                    <tr>
                                        <th colSpan={4}>
                                            Best Score (User)
                                        </th>
                                    </tr>
                                    <tr>
                                        <th key='siteId' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Site
                                                </span>
                                            </div>
                                        </th>
                                        <th key='divId' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Division
                                                </span>
                                            </div>
                                        </th>
                                        <th key='userId' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    User ID
                                                </span>
                                            </div>
                                        </th>
                                        <th key='userName' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    User Name
                                                </span>
                                            </div>
                                        </th>
                                        <th key='score' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Score
                                                </span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        state.summary.userBestScores.map((e, i) => (
                                            <tr key={i}>
                                                <td>{e.siteId}</td>
                                                <td>{e.divId}</td>
                                                <td>{e.userId}</td>
                                                <td>{e.userName}</td>
                                                <td>{Math.floor(e.score)}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                        }
                    </Col>
                    <Col>

                        {
                            state.summary.userMostAttempts &&
                            <Table responsive striped borderless size='sm' className='text-center'>
                                <thead>
                                    <tr>
                                        <th colSpan={4}>
                                            Most Attempts (User)
                                        </th>
                                    </tr>
                                    <tr>
                                        <th key='siteId' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Site
                                                </span>
                                            </div>
                                        </th>
                                        <th key='divId' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Division
                                                </span>
                                            </div>
                                        </th>
                                        <th key='userId' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    User ID
                                                </span>
                                            </div>
                                        </th>
                                        <th key='userName' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    User Name
                                                </span>
                                            </div>
                                        </th>
                                        <th key='score' style={{ minWidth: '150px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Attempts
                                                </span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        state.summary.userMostAttempts.map((e, i) => (
                                            <tr key={i}>
                                                <td>{e.siteId}</td>
                                                <td>{e.divId}</td>
                                                <td>{e.userId}</td>
                                                <td>{e.userName}</td>
                                                <td>{e.attempt}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {
                            state.summary.gameBestAvgScores &&
                            <Table responsive striped borderless size='sm' className='text-center'>
                                <thead>
                                    <tr>
                                        <th colSpan={3}>
                                            Best Average Score (Game)
                                        </th>
                                    </tr>
                                    <tr>
                                        <th key='userId' style={{ minWidth: '200px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Game ID
                                                </span>
                                            </div>
                                        </th>
                                        <th key='userName' style={{ minWidth: '200px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Game Name
                                                </span>
                                            </div>
                                        </th>
                                        <th key='score' style={{ minWidth: '200px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Average Score
                                                </span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        state.summary.gameBestAvgScores.map((e, i) => (
                                            <tr key={i}>
                                                <td>{e.gameId}</td>
                                                <td>{GameIDStrings[e.gameId]}</td>
                                                <td>{Math.floor(e.score)}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                        }
                    </Col>
                    <Col>
                        {
                            state.summary.gameMostAttempts &&
                            <Table responsive striped borderless size='sm' className='text-center'>
                                <thead>
                                    <tr>
                                        <th colSpan={3}>
                                            Most Attempts (Game)
                                        </th>
                                    </tr>
                                    <tr>
                                        <th key='userId' style={{ minWidth: '200px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Game ID
                                                </span>
                                            </div>
                                        </th>
                                        <th key='userName' style={{ minWidth: '200px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Game Name
                                                </span>
                                            </div>
                                        </th>
                                        <th key='score' style={{ minWidth: '200px' }}>
                                            <div className='d-grid'>
                                                <span>
                                                    Attempts
                                                </span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        state.summary.gameMostAttempts.map((e, i) => (
                                            <tr key={i}>
                                                <td>{e.gameId}</td>
                                                <td>{GameIDStrings[e.gameId]}</td>
                                                <td>{e.attempt}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col sm='2' md='3' lg='4'>
                        <canvas id={chartIDs[0]}></canvas>
                    </Col>
                    <Col sm='4' md='6' lg='8'>
                        <canvas id={chartIDs[1]}></canvas>
                    </Col>
                </Row>
                <Row>
                    <div style={{ height: '50px' }}></div>
                </Row>
                <Row>
                    <Col md='6' lg='8'>
                        <canvas id={chartIDs[2]}></canvas>
                    </Col>
                    <Col md='4' lg='2'>
                        <div style={{ height: '120px' }}></div>
                        <p>Total Attempts: {state.totalMonthlyAttempts}</p>
                        <p>Total Users: {state.totalMonthlyUsers}</p>
                    </Col>
                </Row>
                <Row>
                    <Col md='6' lg='8'>
                        <canvas id={chartIDs[3]}></canvas>
                    </Col>
                    <Col md='4' lg='2'>
                    </Col>
                </Row>
                <Row>
                    <div style={{ height: '50px' }}></div>
                </Row>
                <Row>
                    <Table responsive striped borderless size='sm' className='text-center'>
                        <thead>
                            <tr>
                                <th colSpan={7}>
                                    <div className='d-grid'>
                                        <ButtonGroup>
                                            {(currentPaging.items.map((page, index) => {
                                                if (page == -1) return (<Button key={`page.hidden`} variant={ButtonVariant.SecondaryOutline}>...</Button>);
                                                return (<Button key={`page.${page}`} className={page == 1 ? `rounded-start-5 ${state.pages == 1 ? 'rounded-end-5' : ''}` : (page == state.pages ? "rounded-end-5" : "")} variant={(currentPaging.current == page) && ButtonVariant.Secondary || ButtonVariant.SecondaryOutline} onClick={() => doPage(page)}>{page}</Button>);
                                            }))}
                                        </ButtonGroup>
                                    </div>
                                </th>

                                {currentPaging.hasHidden &&
                                    <th colSpan={1}>
                                        <div className='d-grid'>
                                            <InputGroup>
                                                <Form.Control className="rounded-start-5" type='number' placeholder="Go to page..." name='page' value={state.pageJump > 0 ? state.pageJump.toString() : ''} onChange={(e) => {
                                                    updateState({
                                                        pageJump: parseInt(e.target.value),
                                                    });
                                                }} />
                                                <Button className="rounded-end-5" onClick={() => {
                                                    if (state.pageJump != 0) {
                                                        doPage(state.pageJump);
                                                    }
                                                }}>
                                                    <FaBook />
                                                </Button>
                                            </InputGroup>
                                        </div>
                                    </th>
                                }
                            </tr>
                            <tr>
                                <th key='timestamp' style={{ minWidth: '160px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.Timestamp)}>
                                        <span>
                                            Timestamp
                                            {state.sort == EntrySort.Timestamp && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='siteId' style={{ minWidth: '90px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.SiteID)}>
                                        <span>
                                            Site ID
                                            {state.sort == EntrySort.SiteID && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='divId' style={{ minWidth: '90px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.DivID)}>
                                        <span>
                                            Division
                                            {state.sort == EntrySort.DivID && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='userId' style={{ minWidth: '120px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.UserID)}>
                                        <span>
                                            User ID
                                            {state.sort == EntrySort.UserID && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='userName' style={{ minWidth: '300px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.UserName)}>
                                        <span>
                                            Name
                                            {state.sort == EntrySort.UserName && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='compID' style={{ minWidth: '120px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.CompID)}>
                                        <span>
                                            Competency ID
                                            {state.sort == EntrySort.CompID && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='gameID' style={{ minWidth: '120px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.GameID)}>
                                        <span>
                                            Game ID
                                            {state.sort == EntrySort.GameID && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                {/* <th key='attempt' style={{ minWidth: '120px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.Attempt)}>
                                        <span>
                                            Attempt
                                            {state.sort == EntrySort.Attempt && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th> */}
                                <th key='score' style={{ minWidth: '80px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.Score)}>
                                        <span>
                                            Score
                                            {state.sort == EntrySort.Score && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                                <th key='report' style={{ minWidth: '200px' }}>
                                    <div className='d-grid' onClick={() => doSort(EntrySort.Report)}>
                                        <span>
                                            Report
                                            {state.sort == EntrySort.Report && (state.sortType == SortType.Ascending && <FaArrowUp /> || <FaArrowDown />)}
                                        </span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.entries.map((value: Entry, index: number) => {
                                return (
                                    <tr key={`${index}.entry`}>
                                        <td key={`${index}.timestamp`}>
                                            {new Date(value.timestamp).toDateString()}
                                        </td>
                                        <td key={`${index}.siteId`}>
                                            {value.siteId}
                                        </td>
                                        <td key={`${index}.divId`}>
                                            {value.divId}
                                        </td>
                                        <td key={`${index}.userId`}>
                                            {value.userId}
                                        </td>
                                        <td key={`${index}.userName`}>
                                            {value.userName}
                                        </td>
                                        <td key={`${index}.compId`}>
                                            {CompIDStrings[value.compId]}
                                        </td>
                                        <td key={`${index}.gameId`}>
                                            {GameIDStrings[value.gameId]}
                                        </td>
                                        {/* <td key={`${index}.attempt`}>
                                            {value.attempt.toString()}
                                        </td> */}
                                        <td key={`${index}.score`}>
                                            {value.score}
                                        </td>
                                        <td key={`${index}.report`}>
                                            {getReportValueString(value.report).map((report: string, idx: number) => {
                                                return (
                                                    <div key={idx}>
                                                        {report}
                                                        <br />
                                                    </div>
                                                );
                                            })}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <br></br><br></br>
                        <tfoot>
                            <tr>
                                <th>
                                    <div className='d-grid'>
                                        <Button variant={ButtonVariant.Primary} className={`${ButtonStyle.RoundedLeft} ${ButtonStyle.RoundedRight}`}
                                            onClick={doExport}>
                                            Export
                                        </Button>
                                    </div>
                                </th>
                            </tr>
                        </tfoot>
                    </Table>
                </Row>
            </Container>
        </>
    );
};

export default Charts;